<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { subDays } from 'date-fns';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import PageLayout from 'dashboard/components-next/assistant/PageLayout.vue';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import AssistantUsageAPI from 'dashboard/api/assistant/usage';

const { t, te } = useI18n();

const GROUP_BY_OPTIONS = ['day', 'model', 'agent', 'feature', 'product'];

const groupBy = ref('day');
// Диапазон дат — как в отчётах (эталон CSAT): пикер с пресетами + свой период.
const dateRange = ref([subDays(new Date(), 29), new Date()]);
const rangeType = ref(DATE_RANGE_TYPES.LAST_30_DAYS);
const isFetching = ref(false);
const summary = ref(null);
const breakdown = ref([]);

// Р74: счёт и статистика по ДИАЛОГАМ. Три числа расходятся тем сильнее, чем
// дольше живёт переписка, поэтому показываем все три сразу, а не одно.
const SESSION_GAP_OPTIONS = [6, 12, 24, 48];
const sessionGapHours = ref(24);
const dialogues = ref(null);
const isDownloadingDialogues = ref(false);

const dialogueCards = computed(() => {
  const s = dialogues.value?.summary;
  if (!s) return [];
  return [
    { key: 'unique', value: s.dialogues_unique },
    { key: 'sessions', value: s.dialogues_sessions },
    { key: 'started', value: s.dialogues_started },
    { key: 'with_ai', value: s.dialogues_with_ai },
    { key: 'resolved_by_ai', value: s.dialogues_resolved_by_ai },
    { key: 'handed_off', value: s.dialogues_handed_off },
    // Р78: ради чего всё и работает — сколько диалогов дошло до заявки.
    { key: 'with_lead', value: s.dialogues_with_lead },
    { key: 'conversion', value: `${s.conversion_rate}%` },
    { key: 'messages', value: s.messages_total },
    { key: 'avg_messages', value: s.avg_messages_per_dialogue },
    { key: 'cost_per_dialogue', value: s.cost_rub_per_dialogue },
    { key: 'active_clients', value: s.active_clients },
  ].map(card => ({
    ...card,
    label: t(`ASSISTANT.USAGE.DIALOGUES.CARD.${card.key.toUpperCase()}`),
  }));
});

const summaryCards = computed(() => {
  if (!summary.value) return [];
  return [
    // Р63: тарифицируемая единица — впереди денег и токенов, это то, по чему
    // выставляется счёт. Анонимные показаны рядом, чтобы было видно: за них не платят.
    {
      key: 'active_clients',
      label: t('ASSISTANT.USAGE.SUMMARY.ACTIVE_CLIENTS'),
      value: summary.value.active_clients,
    },
    {
      key: 'anonymous_contacts',
      label: t('ASSISTANT.USAGE.SUMMARY.ANONYMOUS_CONTACTS'),
      value: summary.value.anonymous_contacts,
    },
    {
      key: 'events_count',
      label: t('ASSISTANT.USAGE.SUMMARY.EVENTS_COUNT'),
      value: summary.value.events_count,
    },
    {
      key: 'prompt_tokens',
      label: t('ASSISTANT.USAGE.SUMMARY.PROMPT_TOKENS'),
      value: summary.value.prompt_tokens,
    },
    {
      key: 'completion_tokens',
      label: t('ASSISTANT.USAGE.SUMMARY.COMPLETION_TOKENS'),
      value: summary.value.completion_tokens,
    },
    {
      key: 'total_tokens',
      label: t('ASSISTANT.USAGE.SUMMARY.TOTAL_TOKENS'),
      value: summary.value.total_tokens,
    },
    {
      key: 'cost_rub',
      label: t('ASSISTANT.USAGE.SUMMARY.COST_RUB'),
      value: summary.value.cost_rub,
    },
  ];
});

// Р67: в ответе рядом с рублёвыми полями остаются «сырые» *_usd (на них завязаны
// серверные выгрузки), но клиентам-клиникам доллары не показываем — выкидываем
// их и из таблицы, и из CSV (колонки у обеих одни и те же).
const breakdownColumns = computed(() => {
  if (!breakdown.value.length) return [];
  return Object.keys(breakdown.value[0]).filter(
    column => !column.endsWith('_usd')
  );
});

// Колонки приходят именами полей API (day, events_count, cost_rub…). Показывать
// их клинике как есть — инженерный мусор в отчёте; переводим, а незнакомое поле
// оставляем как пришло, чтобы новая колонка на бэке не превратилась в пустую шапку.
const columnLabel = column => {
  const key = `ASSISTANT.USAGE.COL.${column.toUpperCase()}`;
  return te(key) ? t(key) : column;
};

const formatValue = value => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'number') {
    return Number.isInteger(value)
      ? value.toLocaleString('ru-RU')
      : value.toLocaleString('ru-RU', { maximumFractionDigits: 4 });
  }
  return value;
};

// Локальная дата YYYY-MM-DD. НЕ toISOString() — тот отдаёт UTC и для +часовых
// поясов (МСК +3) сдвигает начало диапазона на день назад (пикер отдаёт
// startOfDay/endOfDay в локальном времени).
const pad = n => String(n).padStart(2, '0');
const formatDate = date =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

// Токен запроса: быстрые переключения группировки/периода не должны рисовать
// устаревший ответ, пришедший позже актуального.
let requestToken = 0;
const fetchUsage = async () => {
  isFetching.value = true;
  requestToken += 1;
  const currentToken = requestToken;
  try {
    const { data } = await AssistantUsageAPI.get({
      groupBy: groupBy.value,
      since: formatDate(dateRange.value[0]),
      until: formatDate(dateRange.value[1]),
    });
    if (currentToken !== requestToken) return;
    summary.value = data.summary || null;
    breakdown.value = (data.breakdown || []).map(row => {
      if (row.product) {
        const key = `ASSISTANT.USAGE.PRODUCTS.${row.product.toUpperCase()}`;
        return { ...row, product: te(key) ? t(key) : row.product };
      }
      return row;
    });
  } catch (error) {
    if (currentToken === requestToken) {
      useAlert(t('ASSISTANT.USAGE.API.ERROR_MESSAGE'));
    }
  } finally {
    if (currentToken === requestToken) isFetching.value = false;
  }
};

const setGroupBy = value => {
  if (groupBy.value === value) return;
  groupBy.value = value;
  fetchUsage();
};

const fetchDialogues = async () => {
  try {
    const { data } = await AssistantUsageAPI.conversations({
      since: formatDate(dateRange.value[0]),
      until: formatDate(dateRange.value[1]),
      sessionGapHours: sessionGapHours.value,
    });
    dialogues.value = data;
  } catch (error) {
    dialogues.value = null;
  }
};

const onDateRangeChange = value => {
  const [start, end, type] = value;
  dateRange.value = [start, end];
  rangeType.value = type || DATE_RANGE_TYPES.CUSTOM_RANGE;
  fetchUsage();
  fetchDialogues();
};

// Выгрузка: строим CSV из текущей разбивки на клиенте (BOM для Excel).
const csvEscape = value => {
  let str = value === null || value === undefined ? '' : String(value);
  // Защита от инъекции формул (имена агентов задаёт админ): экранируем
  // ведущие =,+,-,@,tab,CR апострофом, чтобы Excel/Sheets не выполнил формулу.
  if (/^[=+\-@\t\r]/.test(str)) str = `'${str}`;
  return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

const downloadReport = () => {
  const columns = breakdownColumns.value;
  if (!columns.length) {
    useAlert(t('ASSISTANT.USAGE.EMPTY_MESSAGE'));
    return;
  }
  const header = columns.map(columnLabel).join(',');
  const lines = breakdown.value.map(row =>
    columns.map(column => csvEscape(row[column])).join(',')
  );
  //  (BOM) — чтобы Excel корректно открыл UTF-8 (кириллицу).
  const csv = `\ufeff${[header, ...lines].join('\n')}`;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `ai-agents-usage-${formatDate(new Date())}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

// Р63: выгрузка тарифицируемых клиентов — построчно, для сверки счёта.
const isDownloadingClients = ref(false);
const downloadClients = async () => {
  isDownloadingClients.value = true;
  try {
    const { data } = await AssistantUsageAPI.clients({
      since: formatDate(dateRange.value[0]),
      until: formatDate(dateRange.value[1]),
    });
    const rows = data.clients || [];
    if (!rows.length) {
      useAlert(t('ASSISTANT.USAGE.EMPTY_MESSAGE'));
      return;
    }
    const header = [
      t('ASSISTANT.USAGE.CLIENTS_CSV.NAME'),
      t('ASSISTANT.USAGE.CLIENTS_CSV.PHONE'),
      t('ASSISTANT.USAGE.CLIENTS_CSV.CHANNELS'),
      t('ASSISTANT.USAGE.CLIENTS_CSV.FIRST'),
      t('ASSISTANT.USAGE.CLIENTS_CSV.LAST'),
      t('ASSISTANT.USAGE.CLIENTS_CSV.MESSAGES'),
    ].join(',');
    const lines = rows.map(row =>
      [
        row.name,
        row.phone,
        row.channels,
        row.first_message_at,
        row.last_message_at,
        row.messages_count,
      ]
        .map(csvEscape)
        .join(',')
    );
    const csv = `\ufeff${[header, ...lines].join('\n')}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `active-clients-${formatDate(new Date())}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  } catch (error) {
    useAlert(t('ASSISTANT.USAGE.ERROR_MESSAGE'));
  } finally {
    isDownloadingClients.value = false;
  }
};

const setSessionGap = value => {
  sessionGapHours.value = value;
  fetchDialogues();
};

const downloadDialogues = async () => {
  isDownloadingDialogues.value = true;
  try {
    const { data } = await AssistantUsageAPI.dialogues({
      since: formatDate(dateRange.value[0]),
      until: formatDate(dateRange.value[1]),
      sessionGapHours: sessionGapHours.value,
    });
    const rows = data.dialogues || [];
    if (!rows.length) {
      useAlert(t('ASSISTANT.USAGE.EMPTY_MESSAGE'));
      return;
    }
    const columns = [
      'conversation_id',
      'channel',
      'client',
      'phone',
      'started_at',
      'last_message_at',
      'messages_count',
      'ai_replies_count',
      'resolved_by_ai',
      'handed_off',
      'lead',
      'cost_rub',
    ];
    const header = columns
      .map(column => t(`ASSISTANT.USAGE.DIALOGUES.CSV.${column.toUpperCase()}`))
      .join(',');
    const lines = rows.map(row =>
      columns.map(column => csvEscape(row[column])).join(',')
    );
    const csv = `\ufeff${[header, ...lines].join('\n')}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `dialogues-${formatDate(new Date())}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  } catch (error) {
    useAlert(t('ASSISTANT.USAGE.ERROR_MESSAGE'));
  } finally {
    isDownloadingDialogues.value = false;
  }
};

onMounted(() => {
  fetchUsage();
  fetchDialogues();
});
</script>

<template>
  <PageLayout
    :header-title="$t('ASSISTANT.USAGE.HEADER')"
    :description="$t('ASSISTANT.USAGE.DESCRIPTION')"
    :show-pagination-footer="false"
  >
    <!-- Кнопка выгрузки — справа сверху (эталон отчётов CSAT). -->
    <template #headerActions>
      <Button
        :label="$t('ASSISTANT.USAGE.DOWNLOAD_CLIENTS')"
        icon="i-ph-users-three"
        size="sm"
        variant="outline"
        :is-loading="isDownloadingClients"
        @click="downloadClients"
      />
      <Button
        :label="$t('ASSISTANT.USAGE.DOWNLOAD')"
        icon="i-ph-download-simple"
        size="sm"
        @click="downloadReport"
      />
    </template>

    <template #body>
      <!-- Строка фильтров: диапазон дат + группировка (эталон CSAT-фильтров). -->
      <div
        class="flex flex-col flex-wrap w-full gap-3 md:items-center md:flex-row"
      >
        <WootDatePicker
          v-model:date-range="dateRange"
          v-model:range-type="rangeType"
          @date-range-changed="onDateRangeChange"
        />
        <div class="flex flex-wrap items-center gap-1.5">
          <Button
            v-for="option in GROUP_BY_OPTIONS"
            :key="option"
            type="button"
            :label="t(`ASSISTANT.USAGE.GROUP_BY.${option.toUpperCase()}`)"
            size="sm"
            :variant="groupBy === option ? 'solid' : 'faded'"
            :color="groupBy === option ? 'blue' : 'slate'"
            @click="setGroupBy(option)"
          />
        </div>
      </div>

      <div
        v-if="isFetching"
        class="flex items-center justify-center py-10 text-n-slate-11"
      >
        <Spinner />
      </div>

      <template v-else>
        <!-- Р74: счёт по диалогам. «Уникальные» — сколько переписок трогали,
             «обращения» — та же переписка, разрезанная по паузе тишины. -->
        <section v-if="dialogueCards.length" class="mt-6">
          <div class="flex flex-wrap items-center gap-3 mb-3">
            <h3 class="text-sm font-medium text-n-slate-12">
              {{ t('ASSISTANT.USAGE.DIALOGUES.TITLE') }}
            </h3>
            <span class="text-xs text-n-slate-11">
              {{ t('ASSISTANT.USAGE.DIALOGUES.SESSION_GAP') }}
            </span>
            <div class="flex flex-wrap items-center gap-1.5">
              <Button
                v-for="gap in SESSION_GAP_OPTIONS"
                :key="gap"
                type="button"
                :label="t('ASSISTANT.USAGE.DIALOGUES.HOURS', { count: gap })"
                size="sm"
                :variant="sessionGapHours === gap ? 'solid' : 'faded'"
                :color="sessionGapHours === gap ? 'blue' : 'slate'"
                @click="setSessionGap(gap)"
              />
            </div>
            <Button
              :label="t('ASSISTANT.USAGE.DIALOGUES.DOWNLOAD')"
              icon="i-ph-chats-circle"
              size="sm"
              variant="outline"
              :is-loading="isDownloadingDialogues"
              @click="downloadDialogues"
            />
          </div>

          <div class="grid grid-cols-2 gap-4 md:grid-cols-5">
            <div
              v-for="card in dialogueCards"
              :key="card.key"
              class="flex flex-col gap-1 p-4 border rounded-xl border-n-weak bg-n-solid-1"
            >
              <span class="text-xs text-n-slate-11">{{ card.label }}</span>
              <span class="text-lg font-medium text-n-slate-12">
                {{ formatValue(card.value) }}
              </span>
            </div>
          </div>

          <div v-if="dialogues?.daily?.length" class="mt-4 overflow-x-auto">
            <table class="w-full text-sm border-collapse">
              <thead>
                <tr class="border-b border-n-weak">
                  <th
                    v-for="key in [
                      'DATE',
                      'UNIQUE',
                      'SESSIONS',
                      'WITH_LEAD',
                      'MESSAGES',
                      'BY_AI',
                      'COST',
                    ]"
                    :key="key"
                    class="px-3 py-2 font-medium text-start text-n-slate-11"
                  >
                    {{ t(`ASSISTANT.USAGE.DIALOGUES.DAILY.${key}`) }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in dialogues.daily"
                  :key="row.date"
                  class="border-b border-n-weak last:border-b-0"
                >
                  <td class="px-3 py-2 text-n-slate-12">{{ row.date }}</td>
                  <td class="px-3 py-2 text-n-slate-12">
                    {{ row.dialogues_unique }}
                  </td>
                  <td class="px-3 py-2 text-n-slate-12">
                    {{ row.dialogues_sessions }}
                  </td>
                  <td class="px-3 py-2 text-n-slate-12">
                    {{ row.dialogues_with_lead }}
                  </td>
                  <td class="px-3 py-2 text-n-slate-12">
                    {{ row.messages_total }}
                  </td>
                  <td class="px-3 py-2 text-n-slate-12">
                    {{ row.messages_by_ai }}
                  </td>
                  <td class="px-3 py-2 text-n-slate-12">
                    {{ formatValue(row.cost_rub) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="grid gap-4 mt-4 md:grid-cols-2">
            <div v-if="dialogues?.by_channel?.length">
              <h4 class="mb-2 text-xs text-n-slate-11">
                {{ t('ASSISTANT.USAGE.DIALOGUES.BY_CHANNEL') }}
              </h4>
              <div
                v-for="row in dialogues.by_channel"
                :key="row.channel"
                class="flex justify-between px-3 py-2 text-sm border-b border-n-weak last:border-b-0"
              >
                <span class="text-n-slate-12">{{ row.channel }}</span>
                <span class="text-n-slate-11">
                  {{
                    t('ASSISTANT.USAGE.DIALOGUES.CHANNEL_VALUE', {
                      dialogues: row.dialogues,
                      leads: row.dialogues_with_lead,
                    })
                  }}
                </span>
              </div>
            </div>
            <div v-if="dialogues?.by_agent?.length">
              <h4 class="mb-2 text-xs text-n-slate-11">
                {{ t('ASSISTANT.USAGE.DIALOGUES.BY_AGENT') }}
              </h4>
              <div
                v-for="row in dialogues.by_agent"
                :key="row.agent"
                class="flex justify-between px-3 py-2 text-sm border-b border-n-weak last:border-b-0"
              >
                <span class="text-n-slate-12">{{ row.agent }}</span>
                <span class="text-n-slate-11">{{ row.dialogues }}</span>
              </div>
            </div>
          </div>
        </section>

        <div
          v-if="summaryCards.length"
          class="grid grid-cols-2 gap-4 mt-6 md:grid-cols-5"
        >
          <div
            v-for="card in summaryCards"
            :key="card.key"
            class="flex flex-col gap-1 p-4 border rounded-xl border-n-weak bg-n-solid-1"
          >
            <span class="text-xs text-n-slate-11">{{ card.label }}</span>
            <span class="text-lg font-medium text-n-slate-12">
              {{ formatValue(card.value) }}
            </span>
          </div>
        </div>

        <div v-if="breakdown.length" class="mt-6 overflow-x-auto">
          <table class="w-full text-sm border-collapse">
            <thead>
              <tr class="border-b border-n-weak">
                <th
                  v-for="column in breakdownColumns"
                  :key="column"
                  class="px-3 py-2 font-medium text-start text-n-slate-11"
                >
                  {{ columnLabel(column) }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, index) in breakdown"
                :key="index"
                class="border-b border-n-weak last:border-b-0"
              >
                <td
                  v-for="column in breakdownColumns"
                  :key="column"
                  class="px-3 py-2 text-n-slate-12"
                >
                  {{ formatValue(row[column]) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div
          v-else
          class="flex items-center justify-center py-10 text-sm text-n-slate-11"
        >
          {{ $t('ASSISTANT.USAGE.EMPTY_MESSAGE') }}
        </div>
      </template>
    </template>
  </PageLayout>
</template>
